if (!customElements.get("m-collection-list")) {
  class MCollectionList extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.selectors = {
        slideControls: ".m-slider-controls",
        slideContainer: ".m-mixed-layout__wrapper",
        slideInner: ".m-mixed-layout__inner",
      };
      this.classes = {
        grid: 'm:grid',
        hidden: 'm:hidden',
        swiper: 'swiper-container',
        swiperWrapper: 'swiper-wrapper'
      };
      this.domNodes = queryDomNodes(this.selectors, this);

      this.slider = false;

      this.enableSlider = this.dataset.enableSlider === "true";
      this.items = this.dataset.items;
      this.total = this.dataset.total;
      this.autoplay = this.dataset.autoplay === "true";
      this.autoplaySpeed = this.dataset.autoplaySpeed;
      this.paginationType = this.dataset.paginationType;
      this.expanded = this.dataset.expanded === "true";
      this.mobileDisableSlider = this.dataset.mobileDisableSlider === "true";

      this.init();
      document.addEventListener("matchMobile", () => {
        this.init();
      });
      document.addEventListener("unmatchMobile", () => {
        this.init();
      });
    }

    init() {
      const useMobileScroll = MinimogTheme.config.mqlMobile && (!this.enableSlider || this.mobileDisableSlider);

      if (useMobileScroll) {
        if (typeof this.slider === "object") this.destroySlider();
        this.initMobilePeek();
        return;
      }

      this.removeMobilePeekClones();
      if (!this.enableSlider) return;
      if (typeof this.slider === "object") this.destroySlider();
      this.initSlider();
    }

    getPeekScroller() {
      return this.querySelector(".m-mixed-layout--mobile-scroll");
    }

    centerPeekSlide(index, behavior = "auto") {
      const scroller = this.getPeekScroller();
      const inner = this.querySelector(this.selectors.slideInner);
      const slide = inner && inner.children[index];
      if (!scroller || !slide) return;

      const slideRect = slide.getBoundingClientRect();
      const scrollerRect = scroller.getBoundingClientRect();
      const left = scroller.scrollLeft + (slideRect.left - scrollerRect.left) - (scroller.clientWidth - slide.offsetWidth) / 2;
      scroller.scrollTo({ left, behavior });
    }

    initMobilePeek() {
      const scroller = this.getPeekScroller();
      const inner = this.querySelector(this.selectors.slideInner);
      if (!scroller || !inner || inner.children.length < 2) return;

      if (inner.dataset.peekCloned !== "true") {
        const slides = Array.from(inner.children);
        const firstClone = slides[0].cloneNode(true);
        const lastClone = slides[slides.length - 1].cloneNode(true);
        firstClone.setAttribute("aria-hidden", "true");
        lastClone.setAttribute("aria-hidden", "true");
        firstClone.dataset.peekClone = "first";
        lastClone.dataset.peekClone = "last";
        inner.insertBefore(lastClone, slides[0]);
        inner.appendChild(firstClone);
        inner.dataset.peekCloned = "true";
      }

      requestAnimationFrame(() => {
        requestAnimationFrame(() => this.centerPeekSlide(1));
      });

      if (this.peekScrollBound) return;
      this.peekScrollBound = true;

      scroller.addEventListener("scroll", () => {
        window.clearTimeout(this.peekScrollTimer);
        this.peekScrollTimer = window.setTimeout(() => {
          const currentInner = this.querySelector(this.selectors.slideInner);
          if (!currentInner || currentInner.dataset.peekCloned !== "true") return;

          const slides = Array.from(currentInner.children);
          const scrollerCenter = scroller.getBoundingClientRect().left + scroller.clientWidth / 2;
          let closest = 0;
          let minDistance = Infinity;

          slides.forEach((slide, index) => {
            const slideRect = slide.getBoundingClientRect();
            const slideCenter = slideRect.left + slideRect.width / 2;
            const distance = Math.abs(slideCenter - scrollerCenter);
            if (distance < minDistance) {
              minDistance = distance;
              closest = index;
            }
          });

          if (closest === 0) this.centerPeekSlide(slides.length - 2);
          if (closest === slides.length - 1) this.centerPeekSlide(1);
        }, 80);
      }, { passive: true });
    }

    removeMobilePeekClones() {
      const inner = this.querySelector(this.selectors.slideInner);
      if (!inner || inner.dataset.peekCloned !== "true") return;
      inner.querySelectorAll("[data-peek-clone]").forEach((clone) => clone.remove());
      delete inner.dataset.peekCloned;
    }

    initSlider() {
      const { slideContainer, slideControls, slideInner } = queryDomNodes(this.selectors, this);

      const prevButton = slideControls && slideControls.querySelector(".m-slider-controls__button-prev");
      const nextButton = slideControls && slideControls.querySelector(".m-slider-controls__button-next");
      const paginationIcon = `<svg width="65px" height="65px" viewBox="0 0 72 72" aria-hidden="true" xmlns="http://www.w3.org/2000/svg"><circle class="time" stroke-width="5" fill="none" stroke-linecap="round" cx="33" cy="33" r="28"></circle></svg>`;

      const slideItemsLength = slideInner?.childElementCount || 0;

      this.options = {
        slidesPerView: 1.8,
        centeredSlides: true,
        loop: true,
        loopAdditionalSlides: 2,
        spaceBetween: Number.isNaN(parseInt(this.dataset.mobileGutter || this.dataset.gutter, 10))
          ? 0
          : parseInt(this.dataset.mobileGutter || this.dataset.gutter, 10),
        slidesPerGroup: 1,
        showNavigation: true,
        showPagination: true,
        threshold: 8,
        simulateTouch: true,
        touchStartPreventDefault: true,
        touchStartForcePreventDefault: true,
        preventClicks: true,
        preventClicksPropagation: true,
        autoplay: this.autoplay
          ? {
            delay: parseInt(this.autoplaySpeed) * 1000,
          }
          : false,
        pagination:
          this.paginationType == "fraction"
            ? {
              el: this.querySelector(".swiper-pagination"),
              clickable: true,
              type: "fraction",
            }
            : {
              el: this.querySelector(".swiper-pagination"),
              clickable: true,
              bulletClass: "m-dot",
              bulletActiveClass: "m-dot--active",
              renderBullet: function (index, className) {
                return '<span class="' + className + '">' + paginationIcon + "</span>";
              },
            },
        navigation: nextButton || prevButton ? {
          nextEl: nextButton && nextButton,
          prevEl: prevButton && prevButton
        } : false,
        autoHeight: true,
        breakpoints: {
          768: {
            slidesPerView: parseInt(this.items) >= 3 ? 3 : parseInt(this.items),
            centeredSlides: false,
            spaceBetween: Number.isNaN(parseInt(this.dataset.gutter, 10)) ? 0 : parseInt(this.dataset.gutter, 10),
          },
          1280: {
            slidesPerView: this.expanded && this.total > this.items ? parseInt(this.items) + 1 : parseInt(this.items),
            centeredSlides: false,
            spaceBetween: Number.isNaN(parseInt(this.dataset.gutter, 10)) ? 0 : parseInt(this.dataset.gutter, 10),
          },
        },
        on: {
          breakpoint: (swiper, breakpointParams) => {
            if (!slideControls) return;
            const { slidesPerView } = breakpointParams;
            if (slideItemsLength > slidesPerView) {
              slideControls.classList.remove(this.classes.hidden);
              swiper.allowTouchMove = true;
            } else {
              slideControls.classList.add(this.classes.hidden);
              swiper.allowTouchMove = false;
            }
          },
        },
      };

      slideContainer.classList.add(this.classes.swiper);
      slideInner.classList.remove(this.classes.grid);
      slideInner.classList.add(this.classes.swiperWrapper);
      slideControls && slideControls.classList.remove(this.classes.hidden);
      this.slider = new MinimogLibs.Swiper(slideContainer, this.options);
    }

    destroySlider() {
      const { slideContainer, slideControls, slideInner } = queryDomNodes(this.selectors, this);

      slideContainer.classList.remove(this.classes.swiper);
      slideInner.classList.remove(this.classes.swiperWrapper);
      slideInner.classList.add(this.classes.grid);
      slideControls && slideControls.classList.add(this.classes.hidden);

      if (typeof this.slider === 'object') {
        this.slider.destroy();
        this.slider = false;
      }
    }
  }

  customElements.define("m-collection-list", MCollectionList);
}
